export type ToolCategory =
  | 'microeconomics'
  | 'macroeconomics'
  | 'data'
  | 'finance';

export interface ToolMeta {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  route: string;
  category: ToolCategory;
  keywords: string[];
  difficulty: 'intro' | 'intermediate' | 'advanced';
}

export const toolRegistry: ToolMeta[] = [
  {
    id: 'fred-quickchart',
    title: 'FRED QuickChart Macro Explorer',
    shortTitle: 'FRED QuickChart',
    description: 'Visualize historical US macroeconomic indicators including GDP, Inflation, Unemployment, and Interest rates.',
    route: '/tools/fred-quickchart',
    category: 'data',
    keywords: ['fred', 'gdp', 'inflation', 'cpi', 'unrate', 'interest rates', 'm2', 'macroeconomy'],
    difficulty: 'intro'
  },
  {
    id: 'cpi-inflation-calculator',
    title: 'CPI & Inflation Calculator',
    shortTitle: 'CPI Calculator',
    description: 'Adjust monetary values for inflation over time using historical CPI tables or manual index inputs.',
    route: '/tools/cpi-inflation-calculator',
    category: 'macroeconomics',
    keywords: ['inflation', 'cpi', 'purchasing power', 'consumer price index', 'real dollar conversion'],
    difficulty: 'intro'
  },
  {
    id: 'elasticity-solver',
    title: 'Price Elasticity of Demand Solver',
    shortTitle: 'Elasticity Solver',
    description: 'Calculate price elasticity of demand using the midpoint (arc) method and evaluate revenue impact.',
    route: '/tools/elasticity-solver',
    category: 'microeconomics',
    keywords: ['elasticity', 'midpoint method', 'demand', 'revenue effect', 'elastic', 'inelastic'],
    difficulty: 'intro'
  },
  {
    id: 'supply-demand-equilibrium',
    title: 'Linear Supply & Demand Equilibrium',
    shortTitle: 'Supply & Demand',
    description: 'Find market equilibrium price and quantity from linear supply and demand equations.',
    route: '/tools/supply-demand-equilibrium',
    category: 'microeconomics',
    keywords: ['supply', 'demand', 'equilibrium', 'linear equation', 'market clearing price'],
    difficulty: 'intro'
  },
  {
    id: 'tax-subsidy-incidence',
    title: 'Tax & Subsidy Incidence Calculator',
    shortTitle: 'Tax/Subsidy Incidence',
    description: 'Analyze how per-unit taxes or subsidies shift equilibrium price, quantity, tax burden, and deadweight loss.',
    route: '/tools/tax-subsidy-incidence',
    category: 'microeconomics',
    keywords: ['tax', 'subsidy', 'tax incidence', 'deadweight loss', 'buyer burden', 'seller burden'],
    difficulty: 'intermediate'
  },
  {
    id: 'surplus-deadweight-loss',
    title: 'Consumer & Producer Surplus Solver',
    shortTitle: 'Surplus & DWL',
    description: 'Calculate consumer surplus, producer surplus, and total surplus using linear demand and supply choke prices.',
    route: '/tools/surplus-deadweight-loss',
    category: 'microeconomics',
    keywords: ['consumer surplus', 'producer surplus', 'total surplus', 'choke price', 'welfare economics'],
    difficulty: 'intro'
  },
  {
    id: 'gdp-calculator',
    title: 'GDP Components Calculator',
    shortTitle: 'GDP Calculator',
    description: 'Compute Nominal and Real GDP using the expenditure approach (C + I + G + NX) and evaluate component shares.',
    route: '/tools/gdp-calculator',
    category: 'macroeconomics',
    keywords: ['gdp', 'expenditure approach', 'nominal gdp', 'real gdp', 'gdp per capita', 'net exports'],
    difficulty: 'intro'
  },
  {
    id: 'unemployment-rate-calculator',
    title: 'Unemployment & Labor Market Calculator',
    shortTitle: 'Unemployment Rate',
    description: 'Calculate unemployment rate, labor force participation, and employment-population ratios.',
    route: '/tools/unemployment-rate-calculator',
    category: 'macroeconomics',
    keywords: ['unemployment', 'labor force', 'participation rate', 'employment ratio', 'not in labor force'],
    difficulty: 'intro'
  },
  {
    id: 'money-multiplier',
    title: 'Money Multiplier & Creation Solver',
    shortTitle: 'Money Multiplier',
    description: 'Calculate the deposit expansion multiplier under simple reserves or expanded currency/excess reserve leakage.',
    route: '/tools/money-multiplier',
    category: 'macroeconomics',
    keywords: ['money multiplier', 'reserve requirement', 'excess reserve', 'currency drain', 'money creation'],
    difficulty: 'intermediate'
  },
  {
    id: 'exchange-arbitrage',
    title: 'Exchange Rate Triangular Arbitrage Checker',
    shortTitle: 'Exchange Arbitrage',
    description: 'Identify if discrepancies between three currency cross rates create profitable risk-free arbitrage cycles.',
    route: '/tools/exchange-arbitrage',
    category: 'finance',
    keywords: ['arbitrage', 'exchange rate', 'triangular arbitrage', 'currency cross rates', 'forex'],
    difficulty: 'advanced'
  },
  {
    id: 'lorenz-gini',
    title: 'Lorenz Curve & Gini Coefficient Tool',
    shortTitle: 'Lorenz & Gini',
    description: 'Calculate the Gini coefficient of inequality and plot the Lorenz curve from text input or CSV files.',
    route: '/tools/lorenz-gini',
    category: 'data',
    keywords: ['gini coefficient', 'lorenz curve', 'inequality', 'income distribution', 'wealth share'],
    difficulty: 'intermediate'
  },
  {
    id: 'npv-irr-sheet',
    title: 'NPV & IRR Project Evaluation Sheet',
    shortTitle: 'NPV / IRR Sheet',
    description: 'Evaluate investments using Net Present Value (NPV), Internal Rate of Return (IRR), and payback calculations.',
    route: '/tools/npv-irr-sheet',
    category: 'finance',
    keywords: ['npv', 'irr', 'internal rate of return', 'net present value', 'cash flows', 'payback period'],
    difficulty: 'advanced'
  }
];
